import { CssBaseline, Direction, ThemeProvider } from '@mui/material';

// contexts
import { ConfigProvider } from '@/contexts/ConfigContext';
import { LocalizationProvider } from '@/contexts/LocalizationContext';
import { StoreProvider } from '@/contexts/StoreContext';
import { CacheProvider } from '@emotion/react';
import { ServiceProvider } from '@/contexts/ServiceContext';

import { ABTestingProvider } from '@tajawal/web-common/lib';

// components
import ErrorBoundary from '@/app-common/components/ErrorBoundary/ErrorBoundary';
import PageLayout from '@/app-common/layouts/PageLayout';

// hooks
import { useEffect, useMemo, useState } from 'react';
import { useStateOfAppProvider } from '@/hooks/useStateOfAppProvider';
import useFetchUser from '@/hooks/useFetchUser';
import { useRouter } from 'next/router';

// utils
import { getEmotionCache } from '@/utils/style.utils';
import { getTheme } from '@tajawal/ct-web-design-system';
import { initStore, type Store } from '@/stores';
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import { getABTestingAttr } from '@/utils/analytics.utils';

// types
import type { AppMiddlewareValues } from '@/types/middleware.types';
import type { EmotionCache } from '@emotion/cache';
import type FlightsStoreV2 from '@tajawal/web-flights-sdk/lib/types/flights-store.types';
import { NextComponentType, NextPageContext } from 'next';
import Essentials from '@/app-common/components/Essentials/Essentials';

export type AppProviderContainerProps = {
  defaultEmotionCache: EmotionCache;
  pageProps: AppMiddlewareValues;
  component: NextComponentType<NextPageContext, any, AppMiddlewareValues>;
  partnerReferrer?: string;
};

export function AppProvider({
  pageProps,
  component: Component,
  defaultEmotionCache,
  partnerReferrer,
}: AppProviderContainerProps): JSX.Element {
  // eslint-disable-next-line no-console
  const { pathname } = useRouter();
  const [attributes, setAttributes] = useState({});
  const router = useRouter();
  const { store: initialStore, initialState } = pageProps;
  const isMobile = _get(pageProps, 'device.isMobile', false) || _includes(pathname, 'mweb');

  const store = useMemo(
    () =>
      initialStore
        ? initStore({ ...initialStore, isMobile } as Store)
        : initStore({ appStore: { ...initialState } as FlightsStoreV2, isMobile }),
    [initialStore, initialState]
  );
  const { $resources } = useStateOfAppProvider(store);
  const dir = pageProps?.language?.dir ?? 'rtl';

  useFetchUser({ store });

  const clientSideEmotionCache = getEmotionCache(dir);

  useEffect(() => {
    global.store = store;
  }, [store]);

  const userID = store.appStore?.user?.user?.id;
  useEffect(() => {
    setAttributes(
      getABTestingAttr({
        pathName: router?.pathname,
        asPath: router?.asPath,
        userID,
        config: store.appStore?.config,
      })
    );
  }, [store.appStore?.config?.posConfig, router?.asPath, userID]);

  return (
    <>
      <CacheProvider value={defaultEmotionCache || clientSideEmotionCache}>
        <ThemeProvider theme={getTheme(dir as Direction, partnerReferrer)}>
          <CssBaseline />
          <StoreProvider store={store}>
            <ConfigProvider config={store.appStore.config}>
              <ServiceProvider $resources={$resources}>
                <LocalizationProvider pageProps={pageProps}>
                  {/* TODO: Remove ts-ignore once the type is added to core sdk */}
                  {/* @ts-ignore */}
                  <ABTestingProvider appKey={config?.splitAppKey?.flights} attributes={attributes}>
                    <ErrorBoundary>
                      {/* @ts-ignore */}
                      <Essentials splitKey={config?.splitAppKey?.flights} />
                      <PageLayout isMobile={isMobile} store={store}>
                        <Component {...pageProps} store={store} />
                      </PageLayout>
                    </ErrorBoundary>
                  </ABTestingProvider>
                </LocalizationProvider>
              </ServiceProvider>
            </ConfigProvider>
          </StoreProvider>
        </ThemeProvider>
      </CacheProvider>
    </>
  );
}
